import { useState, useMemo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "../../../../components/Drawer";
import Action from "../../Actions/Action";
import ChangeHiringInformation from "./ChangeHiringInformation";
import ChangeJobInformation from "./ChangeJobInformation";
import ChangeOrganisation from "./ChangeOrganisation";
import ChangeUserGroups from "./ChangeUserGroups";
import ChangeEmploymentType from "./ChangeEmploymentType";
import ChangePayCodes from "./ChangePayCodes";
import { ProfileContext } from "../../context";
import { useIsActionDisabled } from "../../Actions/useIsActionDisabled";
import { useAccess } from "../../../../utils/hooks/access";

function Actions() {
	const { t } = useTranslation();
	const [action, setAction] = useState(null);
	const { isSelf, user } = useContext(ProfileContext);
	const getIsActionDisabled = useIsActionDisabled();
	const { hasAccess: canChangeJobInformation } = useAccess(
		isSelf ? "profile.changeJobInformation" : "people.changeJobInformation",
	);

	const { hasAccess: canChangeHiringInfromation } = useAccess(
		isSelf
			? "profile.changeHiringInformation"
			: "people.changeHiringInformation",
	);

	const { hasAccess: canChangeOrganisation } = useAccess(
		isSelf ? "profile.changeOrganisation" : "people.changeOrganisation",
	);

	const { hasAccess: canChangeUserGroups } = useAccess(
		isSelf ? "profile.changeUserGroups" : "people.changeUserGroups",
	);

	const { hasAccess: canManageUsers } = useAccess("canManageUsers");

	const drawerTitle = useMemo(() => {
		switch (action) {
			case "job-information":
				return t("change-job-information");
			case "hiring-information":
				return t("change-hiring-information");
			case "organization":
				return t("change-organization");
			case "user-groups":
				return t("change-user-groups");
			case "employment-type": {
				return t("change-employment-type");
			}
			case "pay-codes": {
				return t("change-pay-codes");
			}
			default:
				return '';
		}
	}, [action, t]);

	const close = useCallback(() => {
		setAction(null);
	}, [setAction]);

	const drawerContent = useMemo(() => {
		switch (action) {
			case "job-information":
				return <ChangeJobInformation close={close} />;
			case "hiring-information":
				return <ChangeHiringInformation close={close} />;
			case "organization":
				return <ChangeOrganisation close={close} />;
			case "user-groups":
				return <ChangeUserGroups close={close} />
			case "employment-type":
				return <ChangeEmploymentType close={close} />
			case "pay-codes":
				return <ChangePayCodes close={close} />
			default:
				return null;
		}
	}, [action, close]);

	return (
		<>
			{canChangeJobInformation && (
				<Action
					title={t("job-information")}
					onClick={() => setAction("job-information")}
					disabled={getIsActionDisabled("jobInformation")}
				>
					{t("change")}
				</Action>
			)}

			{canChangeHiringInfromation && (
				<>
					<hr className="my-4" />
					<Action
						title={t("hiring-information")}
						onClick={() => setAction("hiring-information")}
						disabled={getIsActionDisabled("hiringInformation")}
					>
						{t("change")}
					</Action>
				</>
			)}

			{canChangeOrganisation && (
				<>
					<hr className="my-4" />
					<Action
						title={t("organization")}
						onClick={() => setAction("organization")}
						disabled={getIsActionDisabled("organization")}
					>
						{t("change")}
					</Action>
				</>
			)}

			{canChangeUserGroups && (
				<>
					<hr className="my-4" />
					<Action
						title={t("user-groups")}
						onClick={() => setAction("user-groups")}
					>
						{t("change")}
					</Action>
				</>
			)}

			{canChangeJobInformation &&
				<>
					<hr className="my-4" />
					<Action
						title={t("employment-type")}
						onClick={() => setAction("employment-type")}
						disabled={getIsActionDisabled("employmentType")}
					>
						{t("change")}
					</Action>
				</>
			}

			{(canManageUsers && !isSelf) && (
				<>
					<hr className="my-4" />
					<Action
						title={t("pay-codes")}
						onClick={() => setAction("pay-codes")}
						nonApiOnly={false}
					>
						{t("change")}
					</Action>
				</>
			)}

			<div id="custom-drawer" />

			<Drawer
				title={drawerTitle}
				open={action !== null}
				close={close}
				style={{
					maxWidth: "initial",
				}}
			>
				{drawerContent}
			</Drawer>
		</>
	);
}

export default Actions;
